import { Component, input, output, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SearchSuggestion } from '../../../typesense-search.service';
import { SignalsStoreService } from '../../../signals-store.service';

@Component({
  selector: 'app-autocomplete-product-card',
  imports: [CommonModule],
  templateUrl: './autocomplete-product-card.component.html',
  styleUrl: './autocomplete-product-card.component.scss'
})
export class AutocompleteProductCardComponent {
  #signalsStoreService = inject(SignalsStoreService);

  product = input.required<SearchSuggestion>();

  productClick = output<SearchSuggestion>();
  addToCartClick = output<SearchSuggestion>();

  isLoading = input(false);
  hasSession = input(true);
  active = input(false);

  /**
   * Bundles whose weekly contents have not been published yet carry no final price (the
   * indexed price is 0) and are shown as a range instead, matching the shop card. Guarded on
   * `isBundle && !price` so it can never hide a real price: a bundle that does have one — or
   * a genuinely free product like the farm booklet — keeps rendering the exact amount.
   * Falls back to the price when the backend has not indexed a range for this product.
   */
  showPriceRange = computed(() => {
    const p = this.product();
    return !!p.isBundle && !p.price && !!p.priceRange;
  });

  hasDiscount = computed(() => {
    const p = this.product();
    if (this.showPriceRange()) return false;
    return p.originalPrice && p.originalPrice > (p.price || 0);
  });

  isSubscriptionOnly = computed(() => {
    const p = this.product();
    return p.isSubscription && !p.isALaCarte;
  });

  isOutOfStock = computed(() => !!this.product().stock?.isOut);
  isLowStock = computed(() => !!this.product().stock?.isLow);
  lowStockText = computed(() => {
    const s = this.product().stock;
    return s?.isLow ? `Only ${s.available} left` : null;
  });

  marketStatus = computed(() => this.#signalsStoreService.marketStatus());

  /**
   * Mirrors ProductCardV2MainComponent.showMarketClosedSection(): `!isOpen && !preOrder
   * && !isSubscription`. Any product with subscription capability is exempt — a closed
   * market doesn't stop you subscribing, so the notice would be misleading there.
   * Note this is `isSubscription`, NOT subscription-only: a product that is both
   * subscribable and à-la-carte shows no notice either, matching the shop card.
   *
   * NOTE: the shop card also exempts `product.preOrder`, but preOrder is not part of the
   * Typesense document (neither flat nor inside product_data), so a pre-order product
   * shows this notice even though the shop card would not. The user still reaches the
   * PDP, where the real card applies the correct rule.
   */
  showMarketClosedNotice = computed(() =>
    !this.marketStatus().isOpen && !this.product().isSubscription
  );

  /**
   * Separate from the notice above: while the market is closed no one-time add is allowed,
   * including for dual (subscribable + à-la-carte) products — the shop card routes those to
   * a subscription instead (product-card-v2.component.ts:924), and this dropdown has no
   * subscribe UI, so it falls back to the PDP. canQuickAdd already implies isALaCarte, so
   * gating on isOpen alone is enough; subscription-only products never reach here.
   */
  canQuickAdd = computed(() =>
    this.hasSession() && !!this.product().canQuickAdd && !this.isOutOfStock()
      && this.marketStatus().isOpen
  );

  hasAttributes = computed(() => !!(this.product().attributes?.length));

  hasMultiplePackages = computed(() => (this.product().packages?.length ?? 0) > 1);

  badgeText = computed(() => {
    const p = this.product();
    if (this.isSubscriptionOnly()) return 'Subscription';
    if (p.variantLabel) return p.variantLabel;
    if (this.hasAttributes() || this.hasMultiplePackages()) return 'Multiple options';
    return null;
  });

  onProductClick(event: Event): void {
    event.preventDefault();
    this.productClick.emit(this.product());
  }

  onAddToCart(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.addToCartClick.emit(this.product());
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.onerror = null;
    img.src = 'assets/images/product-placeholder.webp';
  }
}
